import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { grid, colors, distances } from '../../../styles/constants';
import { Transaction } from '../../../types/checkout';
import { NewOperationItem } from '../../types';
import { getAmount, getChangedItems, checkAllItemsSelected } from '../helpers';
import { formatMoney } from '../../../helpers/formatters';

import { H2 } from '../../../components/Typography';
import { Table, Thead, Tr, Th, Tbody } from '../../../components/Table';
import { Button, ButtonGroup } from '../../../components/Buttons';
import RefundItemRow from './RefundItemRow';

type RefundByItemsProps = {
    transaction: Transaction;
    items: NewOperationItem[];
    refundTransaction: (
        accountId: string,
        transaction: Transaction,
        amount: number,
        captureItems: NewOperationItem[]
    ) => Promise<Transaction | undefined>;
    accountId: string;
    isLoading: boolean;
    showSuccessMessage: (refundAmount: number, transaction: Transaction) => void;
};

const RefundByItems = ({
    transaction,
    items,
    refundTransaction,
    accountId,
    isLoading,
    showSuccessMessage,
}: RefundByItemsProps) => {
    const { t, i18n } = useTranslation();
    const lng = i18n.language;
    const initialValues: {
        items: NewOperationItem[];
        openSplitItemsIdx: number[];
    } = {
        items,
        openSplitItemsIdx: [],
    };
    return (
        <Wrapper>
            <Formik
                initialValues={initialValues}
                validate={values => {
                    const invalids = values.items.reduce((errors: NewOperationItem[], item: NewOperationItem) => {
                        if (
                            item.operation.amount > item.persisted.refundableAmount ||
                                item.operation.quantity > item.persisted.refundableQuantity
                        ) {
                            return [...errors, item];
                        }
                        return errors;
                    }, []);
                    if (invalids.length === 0) {
                        return;
                    }
                    return { items: invalids };
                }}
                onSubmit={values => {
                    const changedItems = getChangedItems(values.items);
                    const refundAmount = getAmount(changedItems);
                    refundTransaction(accountId, transaction, refundAmount, changedItems).then(result => {
                        if (result) {
                            showSuccessMessage(refundAmount, result);
                        }
                    });
                }}
            >
                {({ values, errors, handleSubmit, setFieldValue }) => {
                    const changedItems = getChangedItems(values.items);
                    const refundAmount = getAmount(changedItems);

                    const toggleAllItems = (value: boolean) => {
                        const changedItems = values.items.map(item => ({
                            ...item,
                            operation: {
                                amount: value ? item.persisted.refundableAmount : 0,
                                quantity: value ? item.persisted.refundableQuantity : 0,
                                splits: value ? item.persisted.refundableSplits : [],

                            },
                        }));
                        setFieldValue('items', changedItems);
                    };

                    const toggleItem = (item: NewOperationItem, index: number) => {
                        const changedItems = [...values.items];
                        changedItems[index] = item;
                        setFieldValue('items', changedItems);
                        setFieldValue('openSplitItemsIdx', values.openSplitItemsIdx.filter(i => i !== index));
                    };


                    const toggleSplit = (index: number) => {
                        if (values.openSplitItemsIdx.includes(index)) {
                            setFieldValue('openSplitItemsIdx', values.openSplitItemsIdx.filter(i => i !== index));
                        } else {
                            setFieldValue('openSplitItemsIdx', [...values.openSplitItemsIdx, index]);
                        }
                    };

                    const isEditingSplits = values.openSplitItemsIdx.length > 0;

                    return (
                        <form onSubmit={handleSubmit} autoComplete="nope">
                            <HiddenInput autoComplete="false" name="hidden" type="text" />
                            <H2>{t('payments.refund.form.title')}</H2>
                            <Items>
                                <Table>
                                    <Thead>
                                        <Tr>
                                            <Th />
                                            <Th>{t('payments.refund.table.id')}</Th>
                                            <Th>{t('payments.refund.table.description')}</Th>
                                            <Th>{t('payments.refund.table.quantity')}</Th>
                                            <Th>{t('payments.refund.table.amount')}</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {values.items.map((item, i) => (
                                            <RefundItemRow
                                                transaction={transaction}
                                                item={item}
                                                key={item.persisted.orderItem.line_id}
                                                onChange={(changedItem: NewOperationItem) => {
                                                    toggleItem(changedItem, i);
                                                }}
                                                isSplitOpen={values.openSplitItemsIdx.includes(i)}
                                                toggleSplit={() => toggleSplit(i)}
                                            />
                                        ))}
                                    </Tbody>
                                </Table>
                            </Items>
                            {checkAllItemsSelected(values.items) ? (
                                <AllToggle onClick={() => !isEditingSplits && toggleAllItems(false)}>
                                    {t('payments.refund.form.select_no_items')}
                                </AllToggle>
                            ) : (
                                <AllToggle onClick={() => !isEditingSplits && toggleAllItems(true)}>
                                    {t('payments.refund.form.select_all_items')}
                                </AllToggle>
                            )}
                            <ButtonGroup>
                                <OperationButton
                                    type="submit"
                                    disabled={refundAmount === 0 || ((errors && errors.items) || []).length > 0 || isLoading || isEditingSplits}
                                >
                                    {t('payments.refund.form.refund_button', {
                                        amount: formatMoney(lng, refundAmount, transaction.currency),
                                    })}
                                </OperationButton>
                            </ButtonGroup>
                        </form>
                    );
                }}
            </Formik>
        </Wrapper>
    );
};

export default RefundByItems;

const Wrapper = styled.div`
    position: relative;
    min-width: ${grid.spans.span5};
`;

const Items = styled.div`
    padding-top: ${distances.tiny};
`;

const OperationButton = styled(Button)`
    min-width: 220px;
`;

const AllToggle = styled.div`
    display:inline-block;
    color: ${colors.primary};
    font-size: 11px;
    margin-bottom: ${distances.small};
    cursor: pointer;
`;

const HiddenInput = styled.input`
    display: none;
`;
